import { EventsFunctions } from 'Shared/utils/event-emitter';
import { MediaItem } from './item';
import { setValidChoiceClass } from './item_display';

/**
 * Class MediaItemImage
 *
 * Represents a MediaItem of type image.
 */
export const MediaItemImage = new Class({
   Extends: MediaItem,

   /**
    * Returns true if this media Item represents an image that has been marked
    * up.
    */
   hasMarkers: function () {
      let markup = this.data.markup();
      return markup && markup.draw;
   },

   /**
    * Returns true if this media Item represents an image that has been
    * cropped.
    */
   hasCrop: function () {
      let markup = this.data.markup();
      return markup && markup.crop;
   },

   /**
    * Returns just the portion of the markup string that does cropping
    */
   cropMarkupString: function () {
      if (!this.hasCrop()) {
         return '';
      }
      return this.data.markup_string().split(';', 2)[1];
   },

   /**
    * Returns the portion of the markup string that draws the markers
    */
   markersString: function () {
      let markup = this.data.markup_string();
      if (!this.hasCrop() && markup) {
         return markup;
      }

      let markersString = markup?.split(';')?.slice(2)?.join(';');

      return markersString ? markersString : 'null';
   },

   /**
    * Returns a list of menu functions that should be allowed
    * for this media item in it's current state.
    *
    */
   _getMenuOptions: function () {
      return {
         markers: true,
         crop: true,
         fullsize: true,
      };
   },

   /**
    * Creates an appropriate representation for the given MediaTarget and
    * passes it as the only argument to the provided callback when it's
    * ready.
    */
   createRepresentationFor: function (mediaTarget, callback) {
      let size = mediaTarget.options.displaySize;
      let self = this;
      self.whenReady(() => {
         let thumb = new MediaItemImageDisplay(self, {
            size: size,
            useBackground: false, //size === 'thumbnail'
         });
         thumb.whenReady(() => {
            callback(thumb);
         });
      });
   },

   /**
    * Instantiates a MediaItemImageDisplay for this media image
    * whose element will automatically update to reflect any state
    * changes in this item.
    *
    * @param size:string one of the image sizes, default: thumbnail
    */
   createDisplay: function (size) {
      let thumb = new MediaItemImageDisplay(this, {
         size: size,
         useBackground: false,
      });
      return thumb;
   },

   /**
    * Returns the url for this image for the specified size.
    *
    * size: 'thumbnail', 'medium', ...
    */
   getSrc: function (size) {
      return this.data[size]();
   },

   /**
    * Returns a url for an uncropped version of this image (though still with markers).
    * If this image isn't cropped, it returns the url of this  image.
    */
   getUncroppedPreview: function (size) {
      let url = window.shared_constants.GuideURI('PREVIEW_IMAGE');
      let id = this.data.srcid() || this.getID();
      url = url + '/' + id;

      const markersString = this.markersString();
      if (markersString) {
         url = url + '/' + markersString + '/' + size;
      }
      return url;
   },

   /**
    * Returns the underlying imageid that this mediaItem represents.
    */
   getID: function () {
      return this.data.imageid();
   },

   getEncoding: function () {
      return this.data.encoding();
   },

   getType: function () {
      return this.data.type();
   },

   getFilter: function () {
      return 'onlyImages';
   },

   toWikiText: function () {
      // Only add left align on wiki pages.
      if (App.wikiTitle !== undefined) {
         return '[image|' + this.getID() + '|align=left]';
      }

      return '[image|' + this.getID() + ']';
   },

   /**
    * Returns the media type and underlying imageid that this mediaItem
    * represents.
    */
   getGlobalID: function () {
      return 'image:' + this.getID();
   },
});

MediaItem.registerType('GuideImage', MediaItemImage);
MediaItem.registerType('CartImage', MediaItemImage);

/**
 * Represents a DOM element that displays a MediaItemImage
 *
 * options: {
 *    size: one of ('thumbnail, 'small', 'medium', ...). Default: 'thumbnail'
 *
 *    useBackground: if true, the the media item will use a background image to
 *       instead of an img tag. Default: false
 * }
 *
 * Fires Events:
 *    select(mediaItem): when the DOM element is clicked
 */
export { MediaItemImageDisplay };
function MediaItemImageDisplay(mediaItem, options) {
   options = options || {};
   let data = mediaItem.data;
   let mediaClass = options.classOverride || 'mediaImage';
   let self = this;
   this.size = options.size || 'standard';
   this.displayType = options.useBackground ? 'background' : 'img';
   this.mediaItem = mediaItem;

   // The container that holds everything.
   let container = (this.container = new Element(
      'div.mediaItem.' + mediaClass + '[data-imageid="' + mediaItem.getID() + '"]'
   ));

   // Add a menu control (defaults to visible, false === hide)
   if (options.menu !== false) {
      container.adopt(MediaItem.createMenuControl(mediaItem));
   }

   // Listen for click and fire "select" (the library listens for this)
   container.addEvent('click', this.fireEvent.pass(['select', mediaItem], this));

   // Store a reference to this instance with the DOM element, so that other
   // code can always get back to the class from the DOM.
   this.container.store('mediaItem', mediaItem);

   mediaItem.addEvents({
      dataChanged: this._updateDisplay.bind(this),
      loadingChanged: this._updateLoading.bind(this),
      deleted: this.dispose.bind(this),
   });

   if (mediaItem.isReady()) {
      self._updateDisplay();
   } else {
      self._updateLoading(true);
   }
}

Object.append(MediaItemImageDisplay.prototype, {
   /**
    * Returns the DOM element that this instance represents.
    * This is the element that should be injected into the page.
    */
   getElement: function () {
      return this.container;
   },

   /**
    * Calls the readyCallback once the image is in the browser cache.
    *
    * Once the callback is called, it's safe to place this element in the DOM
    * without fear of long load-times.
    */
   whenReady: function (readyCallback) {
      if (this._elementPromise) {
         return this._elementPromise.getValue(readyCallback);
      } else {
         if (readyCallback) {
            readyCallback();
         }
         return true;
      }
   },

   /**
    * Updates the DOM element to reflect the current state of the
    * media item.
    */
   _updateDisplay: function () {
      let mediaItem = this.mediaItem;
      let self = this;
      const size = mediaItem.getLargestImageSize(self.size);
      let imgUrl = mediaItem.getSrc(size || self.size);
      let promise = (this._elementPromise = new Future());

      setValidChoiceClass(mediaItem.isValidChoice(), this.container);

      // Wait until the image we want to show is cached.
      new Asset.image(imgUrl, {
         onLoad: updateImgTag,
      });

      function updateImgTag() {
         if (self.displayType == 'background') {
            let contents = (self._displayElement = new Element('div.carpenter-contents.' + size));
            contents.setStyle('backgroundImage', 'url(' + imgUrl + ')');
            self.container.grab(contents);
         } else {
            let img = self._displayElement;
            if (!img) {
               img = self._displayElement = new Element('img.' + size);
               self.container.grab(img);
            }
            img.set('src', imgUrl);
         }

         // Give the UI thread a chance to catch up.
         (function () {
            promise.resolve(imgUrl);
         }).delay(10);
      }
   },

   _updateLoading: function (isLoading) {
      let overlay = this._overlay();
      if (isLoading) {
         this.container.grab(overlay);
      } else {
         this.whenReady(() => {
            overlay.dispose();
         });
      }
   },

   dispose: function () {
      this.container.dispose();
      this.removeEvents();
   },

   _overlay: function () {
      this._overlayEl = this._overlayEl || new Element('div.loadingOverlay');
      return this._overlayEl;
   },

   inject: function (intoElement, option) {
      return this.container.inject(intoElement, option || 'after');
   },

   hide: function () {
      return this.container.hide();
   },

   show: function () {
      return this.container.show();
   },
});

// Allow Thumbnails to fire events
Object.append(MediaItemImageDisplay.prototype, EventsFunctions);
